// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Shared admin panel shell for analysis nodes.
 * Provides consistent UI for LLM model selection, prompts, datasets, and processing options.
 */
import type { FC } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui'
import { Checkbox, Label } from '@/components/ui'
import { LLMModelSelect } from '@/subsystems/workspaces'
import { Brain } from 'lucide-react'

import type { AnalysisNodeShellProps } from './types'
import { useAnalysisPromptState } from './hooks/useAnalysisPromptState'
import { SectionHeader } from './components/common/SectionHeader'
import { DatasourceSection } from './components/sections/DatasourceSection'
import { DatasetOutputSection } from './components/DatasetOutputSection'

export const AnalysisNodeShell: FC<AnalysisNodeShellProps> = ({
  title,
  description,
  mode: _mode,
  allowedDatasourceTypes,
  orgId,
  workspaceId,
  agentId,
  nodeInstanceId,
  config,
  onChange,
  children,
}) => {
  const state = useAnalysisPromptState({
    orgId,
    workspaceId,
    agentId,
    nodeInstanceId,
    config,
    onChange,
  })

  if (state.settingsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Loading configuration...</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Datasource Card */}
      {allowedDatasourceTypes && allowedDatasourceTypes.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <DatasourceSection
              allowedTypes={allowedDatasourceTypes}
              datasourceType={state.datasourceType}
              datasourceConfig={state.datasourceConfig}
              onTypeChange={state.handlers.handleDatasourceTypeChange}
              onConfigChange={state.handlers.handleDatasourceConfigChange}
              orgId={orgId}
              workspaceId={workspaceId}
              agentId={agentId}
            />
          </CardContent>
        </Card>
      )}

      {/* Main Analysis Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <section>
            <SectionHeader
              icon={<Brain className="h-5 w-5" />}
              title="LLM Model"
              description="Select the model used to perform document analysis."
            />
            <div className="mt-4 max-w-md">
              <LLMModelSelect
                app_id="docreview"
                value={state.modelVariant ?? undefined}
                onValueChange={state.handlers.handleModelChange}
                placeholder="Select model"
                showProviderBadge={true}
              />
            </div>

            <div className="mt-4 flex items-start gap-3">
              <Checkbox
                id="long-context-fallback-toggle"
                checked={state.longContextFallbackEnabled}
                onCheckedChange={value => state.handlers.handleLongContextFallbackEnabledChange(Boolean(value))}
                aria-label="Enable long context fallback model"
              />
              <div className="space-y-1">
                <Label htmlFor="long-context-fallback-toggle" className="text-sm font-medium cursor-pointer">
                  Long context fallback
                </Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, prompts exceeding 100k tokens automatically use the fallback model.
                </p>
                {state.longContextFallbackEnabled && (
                  <div className="mt-2 max-w-md">
                    <LLMModelSelect
                      app_id="docreview"
                      value={state.longContextFallbackModel ?? undefined}
                      onValueChange={state.handlers.handleLongContextFallbackModelChange}
                      placeholder="Select fallback model"
                      showProviderBadge={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </section>

          <DatasetOutputSection
            datasetKey={state.datasetKey}
            datasets={state.datasetList}
            onDatasetKeyChange={state.handlers.handleDatasetKeyChange}
          />

          {children}
        </CardContent>
      </Card>
    </div>
  )
}
